import React from "react";

import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { useTealium } from "@hubcapp/context";

import { TableCard } from "@hubcapp/components";
import { UserType } from "@hubcapp/types";

import { LearnerDetailsProps } from "./types";
import { formatPostcode } from "@ds-developer/frontend";
import Label from "@ds-developer/label";
import MapLink from "@ds-developer/map-link";

const useStyles = makeStyles(theme => ({
  labelSpacing: {
    flexFlow: "row nowrap",
    display: "flex",
    gap: "0.5rem",
  },
  phoneNumber: {
    color: theme.palette.secondary.main,
  },
  learnerDetailsCardHeader: {
    padding: "16px 16px 9px",
  },
}));

const LearnerDetails: React.FC<LearnerDetailsProps> = ({
  loading = false,
  personId,
  name,
  age,
  address,
  phoneNumber,
  pickupAddress,
  sourcedBy,
  onClick = () => {},
}) => {
  const { trackEvent } = useTealium();
  const trackOpenMapLink = () => {
    trackEvent({
      eventCategory: "Menu",
      eventAction: "Interaction",
      eventLabel: "Map",
    });
  };
  const classes = useStyles();
  const addressSource = pickupAddress || address;
  const telHref = `tel:${phoneNumber}`;
  return (
    <TableCard
      className={classes.learnerDetailsCardHeader}
      loading={loading}
      text="Edit"
      action={onClick}
      title="Learner details"
    >
      <TableBody>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>
            <Typography variant="body2" data-test="learner-details-name">
              {`${name?.fullName || ""}`}
            </Typography>
          </TableCell>
        </TableRow>

        {(phoneNumber && (
          <TableRow>
            <TableCell>Phone</TableCell>
            <TableCell>
              <Typography
                variant="body2"
                data-test="learner-details-contact-number"
              >
                <a className={classes.phoneNumber} href={telHref}>
                  {phoneNumber}
                </a>
              </Typography>
            </TableCell>
          </TableRow>
        )) ||
          null}

        <TableRow>
          <TableCell>Address 1</TableCell>
          <TableCell>
            <Typography
              variant="body2"
              data-test="learner-details-address-line-1"
            >
              {addressSource?.line1 || ""}
            </Typography>
          </TableCell>
        </TableRow>

        {(addressSource?.line2 && (
          <TableRow>
            <TableCell>Address 2</TableCell>
            <TableCell>
              <Typography
                variant="body2"
                data-test="learner-details-address-line-2"
              >
                {addressSource?.line2}
              </Typography>
            </TableCell>
          </TableRow>
        )) ||
          null}

        <TableRow>
          <TableCell>Town/city</TableCell>
          <TableCell>
            <Typography variant="body2" data-test="learner-details-city">
              {addressSource?.town}
            </Typography>
          </TableCell>
        </TableRow>
        {addressSource?.postcode && (
          <TableRow>
            <TableCell>Pickup postcode</TableCell>
            <TableCell>
              <Box display="flex">
                <Typography
                  variant="body2"
                  data-test="learner-details-postcode"
                >
                  {formatPostcode(addressSource?.postcode)}
                </Typography>
                <Box paddingLeft={2} data-test="learner-map-link">
                  <MapLink
                    postcode={addressSource?.postcode}
                    onClick={trackOpenMapLink}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>Age</TableCell>
          <TableCell>
            <Typography variant="body2" data-test="learner-details-age">
              {age || "-"}
            </Typography>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Learner ID</TableCell>
          <TableCell>
            <Typography variant="body2" data-test="learner-details-id">
              {personId}
            </Typography>
          </TableCell>
        </TableRow>

        {sourcedBy === UserType.Instructor && (
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell>
              <div className={classes.labelSpacing}>
                <Typography variant="body2" data-test="learner-details-id">
                  Self sourced
                </Typography>
                <Label color="ss">SS</Label>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableCard>
  );
};

export default LearnerDetails;
